import { useTranslation } from 'react-i18next';
import { Arrow, Content, Portal, RadioGroup, RadioItem, Root, Trigger } from '@radix-ui/react-dropdown-menu';
import type { ShopperSearch } from '@/scapi';
import { cn } from '@/lib/utils';
import { Icon } from '@/shared-ui/atoms';
import { SortIcon, TickIcon } from '@/shared-ui/icons';

type SortingOption = ShopperSearch.schemas['ProductSearchSortingOption'];

export interface SortMenuProps {
    options: SortingOption[];
    selected?: string;
    onSelect: (sortId: string) => void;
    className?: string;
}

export const SortMenu = ({ options, selected, onSelect, className }: SortMenuProps) => {
    const { t } = useTranslation('plpFilters');

    if (options.length === 0) return null;

    const selectedOption = options.find((option) => option.id === selected) ?? options[0];

    return (
        <Root modal={false}>
            <Trigger
                data-slot="sort-menu-trigger"
                aria-label={t('sortBy', { label: selectedOption.label })}
                className={cn('flex cursor-pointer items-start gap-1 text-sm leading-[18px]', className)}>
                <span className="underline underline-offset-4">{selectedOption.label}</span>
                <Icon icon={SortIcon} decorative className="size-4" />
            </Trigger>
            <Portal>
                <Content
                    data-slot="sort-menu-content"
                    align="end"
                    sideOffset={8}
                    collisionPadding={8}
                    className={cn(
                        'z-50 w-[253px] rounded-sm bg-background p-4 text-foreground filter-floating',
                        'animate-in fade-in-0 zoom-in-95',
                        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95'
                    )}>
                    <RadioGroup
                        aria-label={t('sort')}
                        value={selectedOption.id}
                        onValueChange={(sortId) => {
                            if (sortId !== selectedOption.id) onSelect(sortId);
                        }}
                        className="flex flex-col gap-3">
                        {options.map((option) => {
                            const isSelected = option.id === selectedOption.id;
                            return (
                                <RadioItem
                                    key={option.id}
                                    value={option.id}
                                    className={cn(
                                        'flex w-full cursor-pointer items-center justify-between gap-2 rounded-sm text-left text-sm leading-[18px] outline-none data-[highlighted]:underline',
                                        isSelected ? 'font-normal' : 'font-light'
                                    )}>
                                    {option.label}
                                    {isSelected && <Icon icon={TickIcon} decorative className="size-4" />}
                                </RadioItem>
                            );
                        })}
                    </RadioGroup>
                    <Arrow width={12} height={8} className="fill-background" />
                </Content>
            </Portal>
        </Root>
    );
};
